import React from 'react'
import styled from '@emotion/styled'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import { useStore } from '../../Store'
import { format, parseISO } from 'date-fns'
import { CREDIT_CARD_FEE } from '../../Constants/costs'

const ApplicationSummaryContainer = styled('div')(({ theme }) => ({
  fontSize: '.75em',
  width: '100%',
  color: theme.palette.primary.main,
  padding: '0 3rem',
  [theme.breakpoints.down('xs')]: {
    padding: '1rem',
    flex: 1,
  },
}))

const ApplicationSummaryBorderStyle = styled('div')(
  ({ theme, thirdParty }) => ({
    boxShadow: thirdParty
      ? null
      : '0 2px 5px rgba(0,0,0,0.16), 0 2px 5px rgba(0,0,0,0.23)',
    borderColor: thirdParty
      ? theme.palette.text.action
      : theme.palette.primary.main,
    borderRadius: thirdParty ? '0' : '3px',
    borderWidth: thirdParty ? '5px' : '1px',
    borderStyle: 'solid',
    height: '100%',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1.2em',
    '& p': {
      margin: 0,
      fontSize: '1em',
    },
  })
)

const SummaryContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  '& p': {
    fontSize: '15px',
    margin: 0,
  },
})

const H4Warper = styled('div')({
  marginTop: '1rem',
})

const SubheaderStyles = styled('h2')({
  fontWeight: 'bold',
  alignSelf: 'center',
  marginBottom: '0',
})

const SubSubheaderStyles = styled('h6')({
  fontWeight: 'bold',
  marginBottom: '5px',
  marginTop: '20px',
  fontSize: '13px',
})

const H4Styled = styled('h6')({
  margin: 0,
})

const DividerStyled = styled(Divider)({
  margin: '1rem 0',
})

const PaymentDetailsContainer = styled('div')({
  display: 'flex',
  fontSize: '0.9em',
  justifyContent: 'space-between',
  '& p': {
    fontSize: '15px',
    margin: 0,
  },
})

const LegsContainer = styled('div')({
  textAlign: 'right',
  width: '100%',
})

const PaymentDetailsLabelsContainer = styled('div')({
  '& p': {
    fontWeight: 'bold',
  },
  textAlign: 'right',
})

const ButtonStyle = styled(Button)({
  marginTop: '1rem',
})

function ApplicationSummary({
  isCOF,
  isMX,
  isBOE,
  formikProps,
  handleEditItinerary,
  thirdParty,
}) {
  const {
    isRoundTrip,
    rushService,
    membershipRequest,
    isMember,
    totalMxProfit,
    totalMxCost,
    totalBoeCost,
    totalCofCost,
    rushServiceCost,
    cstMembership,
    memberDiscount,
  } = useStore((state) => ({
    isRoundTrip: state.isRoundTrip,
    rushService: state.rushService,
    membershipRequest: state.membershipRequest,
    isMember: state.isMember,
    totalMxProfit: state.totalMxProfit,
    totalMxCost: state.totalMxCost,
    totalBoeCost: state.totalBoeCost,
    totalCofCost: state.totalCofCost,
    rushServiceCost: state.rushServiceCost,
    cstMembership: state.cstMembership,
    memberDiscount: state.memberDiscount,
  }))
  const { values = {} } = formikProps
  const {
    l1DepartureDate,
    l1ArrivalAirport,
    //l1ETE,
    l1DepartureAirport,
    // l1ETDLocal,
    // l1ETAZulu,
    l1ETDZulu,
    // l1ETALocal,
    l1ArrivalFir,
    l1DepartureFir,
    l2DepartureDate,
    l2ArrivalAirport,
    //l2ETE,
    l2DepartureAirport,
    // l2ETDLocal,
    // l2ETAZulu,
    l2ETDZulu,
    // l2ETALocal,
    l2ArrivalFir,
    l2DepartureFir,
    //COF && MX
    operator,
    aircraftICAOType,
    tailNumber,
    mtowLbs,
    //MX
    //legs,
    wingspanMts,
    //BOE
    boeType,
    operationType,
  } = values

  let AMOUNT = 0
  let AMOUNT_WITH_TAXES = 0
  let MEMBER_DISCOUNT = memberDiscount
  let REAL_MX_PROFIT = 0

  if (isCOF) {
    AMOUNT = membershipRequest ? totalCofCost - memberDiscount : totalCofCost
    AMOUNT_WITH_TAXES = AMOUNT
  } else if (isMX) {
    const TOTAL_PROFIT_COST_CC = Number(totalMxCost) + Number(totalMxProfit)
    REAL_MX_PROFIT = membershipRequest
      ? Number(totalMxProfit) - memberDiscount
      : Number(totalMxProfit)
    AMOUNT = membershipRequest
      ? TOTAL_PROFIT_COST_CC - memberDiscount
      : TOTAL_PROFIT_COST_CC
    AMOUNT_WITH_TAXES = AMOUNT * CREDIT_CARD_FEE
  } else if (isBOE) {
    AMOUNT = membershipRequest ? totalBoeCost - memberDiscount : totalBoeCost
    AMOUNT_WITH_TAXES = AMOUNT
  }

  const transactionAmount =
    AMOUNT_WITH_TAXES +
    Number(rushService && rushServiceCost) +
    Number(membershipRequest && cstMembership)

  const getLegs = () => {
    // arrivalAirport: "mmox"
    // departureAirport: "cyyz"
    // departureDate: "2021-04-04"
    return values?.legs?.map((leg, index) => {
      return (
        <p style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 'bold' }}>{`Leg ${index + 1}:  `}</span>
          {` ${format(
            parseISO(leg.departureDate),
            'MMM dd, yyyy'
          )} from ${leg.departureAirport.toUpperCase()} to ${leg.arrivalAirport.toUpperCase()}`}
        </p>
      )
    })
  }

  return (
    <>
      <ApplicationSummaryContainer>
        <ApplicationSummaryBorderStyle thirdParty={thirdParty}>
          <>
            <SubheaderStyles>APPLICATION SUMMARY</SubheaderStyles>
            {isCOF && (
              <>
                <SubSubheaderStyles>LEG 1:</SubSubheaderStyles>
                <PaymentDetailsContainer>
                  <p>DATE</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{format(parseISO(l1DepartureDate), 'MMM dd, yyyy')}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>DEPARTURE APT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{`${l1DepartureAirport?.toUpperCase()}`}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ARRIVAL APT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{l1ArrivalAirport?.toUpperCase()}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ETD</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{`${l1ETDZulu} ZULU`}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ENTRY POINT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{l1ArrivalFir}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>EXIT POINT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{l1DepartureFir}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
              </>
            )}
            {isCOF && isRoundTrip ? (
              <>
                <SubSubheaderStyles>LEG 2:</SubSubheaderStyles>
                <PaymentDetailsContainer>
                  <p>DATE</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{format(parseISO(l2DepartureDate), 'MMM dd, yyyy')}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>DEPARTURE APT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{`${l2DepartureAirport?.toUpperCase()}`}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ARRIVAL APT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{l2ArrivalAirport?.toUpperCase()}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ETD</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{`${l2ETDZulu} ZULU`}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ENTRY POINT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{l2ArrivalFir}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>EXIT POINT</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{l2DepartureFir}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
              </>
            ) : null}
            {(isCOF || isMX) && (
              <>
                <SubSubheaderStyles>AIRCRAFT INFORMATION</SubSubheaderStyles>
                <PaymentDetailsContainer>
                  <p>Tail Number</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{tailNumber}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>ICAO Type</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{aircraftICAOType}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>Owner</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{operator}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  {isCOF ? <p>MTOW</p> : <p>Wingspan</p>}
                  <PaymentDetailsLabelsContainer>
                    {isCOF ? (
                      <p>{`${Number(mtowLbs).toLocaleString()} lbs`}</p>
                    ) : (
                      <p>{`${Number(wingspanMts).toLocaleString()} meters`}</p>
                    )}
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
              </>
            )}
            {isBOE && (
              <>
                <SubSubheaderStyles>OPERATOR INFORMATION</SubSubheaderStyles>
                <PaymentDetailsContainer>
                  <p>Name</p>
                  <PaymentDetailsLabelsContainer>
                    <p>{operator}</p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
                <PaymentDetailsContainer>
                  <p>Nature of Operation per CBP regulations</p>
                  <PaymentDetailsLabelsContainer>
                    <p>
                      {operationType === 'COMMERCIAL'
                        ? 'Commercial Aircraft'
                        : 'Non-Commercial Aircraft'}
                    </p>
                  </PaymentDetailsLabelsContainer>
                </PaymentDetailsContainer>
              </>
            )}
            {isMX && (
              <>
                <SubSubheaderStyles>MEXICAN AIRSPACES</SubSubheaderStyles>
                <PaymentDetailsContainer>
                  <LegsContainer>{getLegs()}</LegsContainer>
                </PaymentDetailsContainer>
              </>
            )}

            {handleEditItinerary && (
              <ButtonStyle
                variant="outlined"
                color="secondary"
                onClick={handleEditItinerary}
                fullWidth
              >
                {isCOF ? 'EDIT ITINERARY' : 'EDIT INFORMATION'}
              </ButtonStyle>
            )}
            <H4Warper style={{ marginTop: '40px' }}>
              <H4Styled>{`${(isCOF && 'CUBAN OVERFLIGHT PERMIT') || ''}${
                (isBOE && `BORDER OVERFLIGHT EXEMPTION - ${boeType}`) || ''
              }${(isMX && 'MEXICAN AIRSPACES') || ''}`}</H4Styled>
            </H4Warper>
            {!isMX && (
              <SummaryContainer>
                <p>Subtotal:</p>
                {isMember ? (
                  <p>$ {(AMOUNT_WITH_TAXES + MEMBER_DISCOUNT).toFixed(2)}</p>
                ) : (
                  <p>$ {AMOUNT_WITH_TAXES.toFixed(2)}</p>
                )}
              </SummaryContainer>
            )}
            {isMX && (
              <SummaryContainer>
                <p>Airspace fees payment:</p>
                {isMember ? (
                  <p>$ {(REAL_MX_PROFIT + MEMBER_DISCOUNT).toFixed(2)}</p>
                ) : (
                  <p>$ {REAL_MX_PROFIT.toFixed(2)}</p>
                )}
              </SummaryContainer>
            )}
            {isMX && (
              <SummaryContainer>
                <p>Mexico Tax payment:</p>
                <p>$ {Number(totalMxCost).toFixed(2)}</p>
              </SummaryContainer>
            )}
            {isMX && (
              <SummaryContainer>
                <p>4% Credit Card Commission:</p>
                <p>$ {Number(AMOUNT_WITH_TAXES - AMOUNT).toFixed(2)}</p>
              </SummaryContainer>
            )}
            {rushService && (
              <SummaryContainer style={{ color: '#c40505' }}>
                <p>Rush Service Fee:</p>
                <p>$ {rushServiceCost.toFixed(2)}</p>
              </SummaryContainer>
            )}
            {membershipRequest && (
              <SummaryContainer>
                <p>Membership:</p>
                <p>$ {Number(cstMembership).toFixed(2)}</p>
              </SummaryContainer>
            )}
            {isMember && MEMBER_DISCOUNT > 0 && (
              <SummaryContainer style={{ color: '#D09F53' }}>
                <p>Member discount:</p>
                <p>- $ {MEMBER_DISCOUNT.toFixed(2)}</p>
              </SummaryContainer>
            )}
            <DividerStyled style={{ marginTop: '20px' }} />
            <SubheaderStyles style={{ marginTop: '5px' }}>
              TOTAL ${transactionAmount.toFixed(2)} USD
            </SubheaderStyles>
          </>
        </ApplicationSummaryBorderStyle>
      </ApplicationSummaryContainer>
    </>
  )
}

export default ApplicationSummary
